import React from 'react';
import { useTranslation } from 'react-i18next';

const EMPTY = '—';

function formatVal(v) {
  if (v === undefined || v === null) return EMPTY;
  const s = typeof v === 'boolean' ? String(v) : String(v).trim();
  return s === '' ? EMPTY : s;
}

/**
 * Renders every parsed LV summary field with labels. Used on Properties page and Rent order page.
 * Shows all available data we fetch, all the time.
 */
function LvSummaryAll({ summary, className = '', compact = false }) {
  const { t } = useTranslation();

  if (!summary || typeof summary !== 'object') {
    return <p className="text-sm text-slate-500">{t('rentOrder.fromLvIntro')}</p>;
  }

  const row = (labelKey, value) => (
    <div key={labelKey} className={compact ? 'text-xs' : 'text-sm'}>
      <span className="font-medium text-slate-600 shrink-0">{t(labelKey)}:</span>{' '}
      <span className="text-slate-800">{formatVal(value)}</span>
    </div>
  );

  const section = (titleKey, children) => (
    <div key={titleKey} className="space-y-1.5">
      <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mt-3 first:mt-0">
        {t(titleKey)}
      </h3>
      {children}
    </div>
  );

  const clients = Array.isArray(summary.clients) ? summary.clients : [];

  return (
    <div className={`space-y-1 ${className}`}>
      {section('lvDetail.sectionLv', (
        <>
          {row('lvDetail.lvCislo', summary.lvCislo)}
          {row('lvDetail.katastralniUzemi', summary.katastralniUzemi)}
          {row('lvDetail.obec', summary.obec)}
          {row('lvDetail.okres', summary.okres)}
          {row('lvDetail.katastralniUrad', summary.katastralniUrad)}
          {row('lvDetail.katastralniUradPro', summary.katastralniUradPro)}
          {row('lvDetail.katastralniPracoviste', summary.katastralniPracoviste)}
        </>
      ))}

      {section('lvDetail.sectionOwners', (
        <>
          {clients.length > 0 &&
            clients.map((c, i) => (
              <div key={`c-${i}`} className="pl-2 border-l-2 border-slate-200 space-y-0.5 my-1.5">
                <div className={compact ? 'text-xs' : 'text-sm'}>
                  <span className="font-medium text-slate-600">
                    {t('lvDetail.ownerN', { n: i + 1 })}:
                  </span>{' '}
                  {formatVal(c.clientName)}
                </div>
                <div className="text-xs text-slate-600">
                  {t('lvDetail.clientBirthDates')}: {formatVal(c.clientBirthDates)} ·{' '}
                  {t('lvDetail.clientAddress')}: {formatVal(c.clientAddress)}
                </div>
              </div>
            ))}
          {row('lvDetail.clientName', summary.clientName)}
          {row('lvDetail.clientAddress', summary.clientAddress)}
          {row('lvDetail.clientBirthDates', summary.clientBirthDates)}
          {row('lvDetail.clientCharOs', summary.clientCharOs)}
        </>
      ))}

      {section('lvDetail.sectionUnit', (
        <>
          {row('lvDetail.hasUnit', summary.hasUnit)}
          {row('lvDetail.hasStavbaOrPozemky', summary.hasStavbaOrPozemky)}
          {row('lvDetail.vyuziti', summary.vyuziti)}
          {row('lvDetail.unitNumber', summary.unitNumber)}
          {row('lvDetail.unitNumberOnly', summary.unitNumberOnly)}
          {row('lvDetail.buildingNumber', summary.buildingNumber)}
          {row('lvDetail.castObce', summary.castObce)}
          {row('lvDetail.podilSpolCasti', summary.podilSpolCasti)}
          {row('lvDetail.podilPozemek', summary.podilPozemek)}
          {row('lvDetail.lvStavba', summary.lvStavba)}
          {row('lvDetail.lvPozemek', summary.lvPozemek)}
        </>
      ))}

      {section('lvDetail.sectionParcels', (
        <>
          {row('lvDetail.parcelNumber', summary.parcelNumber)}
          {row('lvDetail.parcelB', summary.parcelB)}
          {row('lvDetail.vymeraParcely', summary.vymeraParcely)}
          {row('lvDetail.druhPozemku', summary.druhPozemku)}
          {row('lvDetail.vymeraB', summary.vymeraB)}
          {row('lvDetail.druhPozemkuB', summary.druhPozemkuB)}
          {Array.isArray(summary.parcelsDetailed) && summary.parcelsDetailed.length > 0 && (
            <div className="space-y-2 mt-2">
              <div className={compact ? 'text-xs' : 'text-sm'}>
                <span className="font-medium text-slate-600">{t('lvDetail.parcelsDetailedHeading')}:</span>
              </div>
              {summary.parcelsDetailed.map((p, i) => (
                <div
                  key={`parcel-det-${i}-${p.id || p.label || i}`}
                  className="pl-2 border-l-2 border-slate-200 space-y-0.5 my-1.5"
                >
                  <div className={compact ? 'text-xs' : 'text-sm'}>
                    <span className="font-medium text-slate-600">{t('lvDetail.parcelLabel')}:</span>{' '}
                    <span className="text-slate-800">{formatVal(p.label)}</span>
                  </div>
                  <div className="text-xs text-slate-600 space-y-0.5">
                    <div>
                      {t('lvDetail.parcelId')}: {formatVal(p.id)} · {t('lvDetail.parcelTel')}:{' '}
                      {formatVal(p.cisloTel)}
                      {p.telId ? ` (${formatVal(p.telId)})` : ''}
                    </div>
                    <div>
                      {t('lvDetail.vymeraParcely')}: {formatVal(p.vymera)} · {t('lvDetail.druhPozemku')}:{' '}
                      {formatVal(p.druhPoz)}
                    </div>
                    {p.zpVyuziti ? (
                      <div>
                        {t('lvDetail.parcelZpVyuziti')}: {formatVal(p.zpVyuziti)}
                      </div>
                    ) : null}
                    {Array.isArray(p.sources) && p.sources.length > 0 ? (
                      <div>
                        {t('lvDetail.parcelSources')}: {p.sources.join(', ')}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ))}
    </div>
  );
}

export default LvSummaryAll;
